import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City } from './city';
import type { Vec } from '../destruction/structure';

export interface NavalLanding {
  id: string;
  building: number;
  berth: Vec;
  door: Vec;
  shore: Vec;
  path: Vec[];
  support: number;
}
export interface StrategicTransportLayout {
  runway: { start: Vec; end: Vec; stand: Vec; supports: number[] };
  landings: NavalLanding[];
}

/** Authored transport surfaces extend the actual city. Aircraft share a short
 * fictional-scale strip; deep-water berths have piled, damageable walkways all
 * the way back to an existing public pavement. */
export function prepareStrategicTransport(city: City): City {
  if (!city.strategic || city.strategic.transport) return city;
  const layout: StrategicTransportLayout = {
    runway: {
      start: { x: -993, y: GROUND + 0.235, z: 365 },
      end: { x: -700, y: GROUND + 0.235, z: 365 },
      stand: { x: -865, y: GROUND + 0.235, z: 365 },
      supports: [],
    },
    landings: [],
  };
  city.strategic.transport = layout;
  let serial =
    Math.max(
      ...[...city.buildings, ...city.props!, ...city.infrastructure!, ...city.bridges!].map((b) => b.id),
    ) + 1;
  for (let section = 0; section < 8; section++) {
    const b: Building = {
      id: serial++,
      name: `Garrison airstrip section ${section + 1}`,
      kind: 'paving',
      category: 'prop',
      district: 'military',
      x: -1010 + section * 40,
      z: 346.25,
      baseY: GROUND,
      w: 32,
      d: 30,
      h: 1,
      color: 0x747d74,
      accent: 0xc7cab0,
      cells: [],
      exactGeometry: true,
    };
    for (let x = 0; x < 32; x++)
      for (let z = 0; z < 30; z++) {
        const id = b.id * 1e6 + key(x, 0, z);
        b.cells.push({
          id,
          x,
          y: 0,
          z,
          root: true,
          material: 'concrete',
          role: 'slab',
          hp: MATERIALS.concrete.strength,
          color: z === 0 || z === 29 || (z === 15 && x % 12 < 6) ? 0xd4d6bb : 0x747d74,
          bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.2 / CELL, d: 1 },
        });
        if (x % 8 === 0 && z === 15) layout.runway.supports.push(id);
      }
    city.infrastructure!.push(b);
    city.strategic.parcels[0].buildings.push(b.id);
  }
  for (const [i, x] of [-520, -180, 620].entries()) {
    const b: Building = {
      id: serial++,
      name: i === 0 ? 'Garrison naval supply pier' : `Coastal heavy-craft berth ${i}`,
      kind: 'paving',
      category: 'prop',
      district: i === 0 ? 'military' : 'coast',
      x: x - 2.5,
      z: 655,
      baseY: -8.5,
      w: 4,
      d: 56,
      h: 2,
      color: 0x9caa9b,
      accent: 0x63756d,
      cells: [],
      exactGeometry: true,
    };
    // Stable cell keys support the deck and distinct piles. The long pier is
    // split into <=32-cell pieces, retaining the shared cell ID convention.
    let endBuilding = b;
    for (let part = 0; part < 2; part++) {
      const section: Building = { ...b, id: part ? serial++ : b.id, z: 655 + part * 35, d: 28, cells: [] };
      for (let z = 0; z < 28; z++)
        for (let xx = 0; xx < 4; xx++) {
          section.cells.push({
            id: section.id * 1e6 + key(xx, 1, z),
            x: xx,
            y: 1,
            z,
            material: 'concrete',
            role: 'slab',
            hp: MATERIALS.concrete.strength,
            color: z % 6 === 0 ? 0xb5bdac : 0x9caa9b,
            bounds: { x: 0, y: (0.18 + 8.5) / CELL - 1, z: 0, w: 1, h: 0.2 / CELL, d: 1 },
          });
          if (z % 4 === 0 && (xx === 0 || xx === 3))
            section.cells.push({
              id: section.id * 1e6 + key(xx, 0, z),
              x: xx,
              y: 0,
              z,
              root: true,
              material: 'concrete',
              role: 'column',
              hp: MATERIALS.concrete.strength,
              color: 0x667a72,
              bounds: { x: 0.35, y: 0, z: 0.35, w: 0.3, h: 8.68 / CELL, d: 0.3 },
            });
        }
      city.props!.push(section);
      endBuilding = section;
    }
    const nearest = city
      .roadGraph!.nodes.filter(
        (n) => !city.roadGraph!.edges.some((e) => (e.from === n.id || e.to === n.id) && e.restricted),
      )
      .sort((a, b) => Math.hypot(a.x - x, a.z - 650) - Math.hypot(b.x - x, b.z - 650))[0];
    const pavement = { x: nearest.x + 6.5, y: 0.38, z: nearest.z + 6.5 },
      shore = { x, y: 0.38, z: 655 },
      door = { x: x + 2.5, y: 0.38, z: 706.25 };
    const path = [pavement, { x, y: 0.38, z: pavement.z }, shore, { x, y: 0.38, z: 706.25 }, door];
    // Only the land-side connection needs new slabs; the piled deck above is
    // the authoritative support from z655 onward.
    const occupied = new Set<string>(),
      tiles = new Map<string, Building>();
    for (let segment = 1; segment <= 2; segment++) {
      const a = path[segment - 1],
        c = path[segment],
        length = Math.hypot(c.x - a.x, c.z - a.z),
        steps = Math.ceil(length / CELL);
      for (let step = 0; step <= steps; step++) {
        const px = Math.floor((a.x + ((c.x - a.x) * step) / Math.max(1, steps)) / CELL),
          pz = Math.floor((a.z + ((c.z - a.z) * step) / Math.max(1, steps)) / CELL);
        for (let lateral = -1; lateral <= 1; lateral++) {
          const gx = px + (Math.abs(c.z - a.z) > 0.1 ? lateral : 0),
            gz = pz + (Math.abs(c.x - a.x) > 0.1 ? lateral : 0),
            tag = `${gx},${gz}`;
          if (occupied.has(tag) || gz * CELL >= 655) continue;
          occupied.add(tag);
          const cx = Math.floor(gx / 32),
            cz = Math.floor(gz / 32),
            chunk = `${cx},${cz}`;
          let tile = tiles.get(chunk);
          if (!tile) {
            tile = {
              id: serial++,
              name: 'Naval pier public access',
              kind: 'paving',
              category: 'prop',
              district: 'coast',
              x: cx * 32 * CELL,
              z: cz * 32 * CELL,
              baseY: GROUND,
              w: 32,
              d: 32,
              h: 1,
              color: 0xaaa994,
              accent: 0xaaa994,
              exactGeometry: true,
              cells: [],
            };
            tiles.set(chunk, tile);
            city.props!.push(tile);
          }
          const lx = gx - cx * 32,
            lz = gz - cz * 32;
          tile.cells.push({
            id: tile.id * 1e6 + key(lx, 0, lz),
            x: lx,
            y: 0,
            z: lz,
            root: true,
            material: 'concrete',
            role: 'slab',
            hp: MATERIALS.concrete.strength,
            color: tile.color,
            bounds: { x: 0, y: 0, z: 0, w: 1, h: (0.38 - GROUND) / CELL, d: 1 },
          });
        }
      }
    }
    city.coast!.paths.push({ id: `naval-access:${i}`, points: path, width: 2.5 });
    layout.landings.push({
      id: `naval:${i}`,
      building: endBuilding.id,
      berth: { x: x + 2.5 + 5.75 + 0.35, y: -0.504, z: 706.25 },
      door,
      shore,
      path,
      support: endBuilding.id * 1e6 + key(3, 1, 13),
    });
  }
  return city;
}
